import React from "react";
import { titleCase } from "../utils";

// ─── PipelinePositions ──────────────────────────────────────────────────────────
// Compact status pills from the task's own pipeline. Never hardcode a status list —
// a position added in 2029 appears with no front-end change (spec §4).
// Tracker (Item 1): pills are text-only (no icons) in a flex-wrap row so any number
// of pipeline positions stays compact without overflow. The active pill (matching
// currentStatus) is filled blue with white text and is not clickable; inactive pills
// are muted neutral and clickable to move the task. Each click calls
// onPositionClick(code, label) and stops propagation so the slide-out's own click
// handler isn't triggered.
const PipelinePositions = ({ positions, currentStatus, onPositionClick }) => {
  if (!positions || positions.length === 0) return null;

  return (
    <div className="px-5 pt-2 pb-3 border-b border-gray-200 dark:border-gray-800">
      {/* Labeled "Status" (not "Pipeline") to avoid confusion with the Pipeline
          filter dropdown in WorkFilters, which refers to work_type (delivery,
          editorial, etc.). These pills are the task's status/position within
          its own pipeline. */}
      <p className="text-xs text-neutral-500 mb-1">Status</p>
      {/* Pill row — desktop only (md+). On mobile a single dropdown replaces
          the pills (Item 4) so five statuses don't compete for horizontal
          space. hidden md:flex keeps the existing flex-wrap pill layout. */}
      <div className="hidden md:flex flex-wrap gap-1.5">
        {positions.map((pos) => {
          const isActive = pos.key === currentStatus;
          const clickable = !isActive && typeof onPositionClick === "function";
          const label = titleCase(pos.label);

          // Active pill: filled blue, white text — not clickable (no cursor/hover).
          // Inactive pills: muted neutral with hover affordance, clickable.
          const stateClass = isActive
            ? "bg-blue-500 text-white"
            : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 dark:hover:text-neutral-200";
          const baseClass = isActive
            ? "text-xs px-2 py-1 rounded-full transition-colors"
            : "text-xs px-2 py-1 rounded-full cursor-pointer transition-colors";

          // Active position (or no handler wired) — not clickable span.
          if (!clickable) {
            return (
              <span key={pos.key || pos.label} className={`${baseClass} ${stateClass}`}>
                {label}
              </span>
            );
          }

          // Non-active position — clickable button that moves the task.
          return (
            <button
              key={pos.key || pos.label}
              type="button"
              className={`${baseClass} ${stateClass}`}
              onClick={(e) => {
                e.stopPropagation();
                onPositionClick(pos.key, label);
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Status dropdown — mobile only (below md). Replaces the pill row so a
          long pipeline (In Progress / Needs Assistance / Complete / Canceled …)
          doesn't wrap across multiple lines on a narrow screen (Item 4).
          Shows the current status as the selected value; choosing another
          status calls the same onPositionClick(code, label) handler as the
          pills. block md:hidden ensures it never shows alongside the pills. */}
      <select
        className="block md:hidden w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        value={currentStatus ?? ""}
        onChange={(e) => {
          e.stopPropagation();
          const key = e.target.value;
          const pos = positions.find((p) => p.key === key);
          if (pos && typeof onPositionClick === "function") {
            onPositionClick(pos.key, titleCase(pos.label));
          }
        }}
      >
        {/* Placeholder when no position matches currentStatus (e.g. loading). */}
        {!positions.some((p) => p.key === currentStatus) && (
          <option value="" disabled>
            Select status…
          </option>
        )}
        {positions.map((pos) => (
          <option key={pos.key || pos.label} value={pos.key}>
            {titleCase(pos.label)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PipelinePositions;
